import { Injectable } from '@angular/core';
import { ItemClassification } from '@boma/supplier-portal/shared/entities';
import { ItemClassificationsService } from '@boma/supplier-portal/shared/services';
import { Store } from '@ngrx/store';
import { BehaviorSubject, map, Observable, switchMap, Subject, takeUntil } from 'rxjs';
import { getSelectedOrganizationInfo } from '../shared-state';

export interface ItemClassificationState {
  itemClassificationSearchResults: ItemClassification[];
  searchTerm: string | null;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class ItemClassificationFacade {
  private readonly _entityId$: Observable<string>;
  private readonly _state$: BehaviorSubject<ItemClassificationState> =
    new BehaviorSubject<ItemClassificationState>({
      itemClassificationSearchResults: [],
      searchTerm: null,
      loading: false,
      error: null,
    });
  private readonly _searchCancellation$ = new Subject<void>();

  loading$ = this._state$.pipe(map((state) => state.loading));
  error$ = this._state$.pipe(map((state) => state.error));
  itemClassificationSearchResults$ = this._state$.pipe(
    map((state) => state.itemClassificationSearchResults)
  );
  searchTerm$ = this._state$.pipe(map((state) => state.searchTerm));

  constructor(
    private store: Store,
    private itemClassificationsService: ItemClassificationsService
  ) {
    this._entityId$ = this.store
      .select(getSelectedOrganizationInfo)
      .pipe(map((info) => info.id));
  }

  searchItemClassifications(searchTerm: string): void {
    this._updateState({ loading: true, error: null, searchTerm });
    
    // Cancel any ongoing search
    this._searchCancellation$.next();
    
    this._entityId$
      .pipe(
        switchMap((entityId) =>
          this.itemClassificationsService.searchItemClassificationCodes(
            entityId,
            searchTerm
          )
        ),
        takeUntil(this._searchCancellation$)
      )
      .subscribe({
        next: (results) => {
          this._updateState({
            itemClassificationSearchResults: results,
            loading: false,
          });
        },
        error: (error) => {
          this._updateState({ error: error?.message, loading: false });
        },
      });
  }

  private _updateState(state: Partial<ItemClassificationState>) {
    this._state$.next({ ...this._state$.value, ...state });
  }
}
